import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import "../styles/TrainingBookingStatus.css";

const GET_TRAININGS = gql`
  query GetTrainings($search: String) {
    getTrainings(search: $search) {
      id
      title
      amount
      gst
      totalAmount
      days
      shortDescription
      fullDescription
    }
  }
`;

const DELETE_TRAINING = gql`
  mutation DeleteTraining($id: ID!) {
    deleteTraining(id: $id)
  }
`;

const GET_ENROLLMENTS = gql`
  query GetEnrollments {
    getEnrollments {
      id
      courseTitle
      courseDays
      totalAmount
      name
      email
      phone
      status
      createdAt
    }
  }
`;

const DELETE_ENROLLMENT = gql`
  mutation DeleteEnrollment($id: ID!) {
    deleteEnrollment(id: $id)
  }
`;

const ITEMS_PER_PAGE = 10;
const STUDENTS_PER_PAGE = 10;

export default function TrainingBookingStatus() {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentSearchTerms, setStudentSearchTerms] = useState({});
  const [studentPages, setStudentPages] = useState({});

  const { loading: loadingTrainings, error: errorTrainings, data: trainingsData, refetch: refetchTrainings } = useQuery(GET_TRAININGS, {
    variables: { search: searchTerm },
  });

  const { loading: loadingEnrollments, error: errorEnrollments, data: enrollmentsData, refetch: refetchEnrollments } = useQuery(GET_ENROLLMENTS);

  const [deleteTraining, { loading: deleting }] = useMutation(DELETE_TRAINING, {
    onCompleted: () => {
      refetchTrainings();
      setExpandedCourse(null);
    },
    onError: (err) => alert("❌ Error deleting: " + err.message),
  });

  const [deleteEnrollment, { loading: deletingEnrollment }] = useMutation(DELETE_ENROLLMENT, {
    onCompleted: () => {
      refetchEnrollments();
      alert("✅ Student enrollment deleted successfully!");
    },
    onError: (err) => alert("❌ Error deleting enrollment: " + err.message),
  });

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this training course?")) {
      deleteTraining({ variables: { id } });
    }
  };

  const handleDeleteEnrollment = (enrollmentId, studentName) => {
    if (window.confirm(`Are you sure you want to delete enrollment for ${studentName}?`)) {
      deleteEnrollment({ variables: { id: enrollmentId } });
    }
  };

  const toggleCourseExpansion = (courseTitle) => {
    const newExpanded = expandedCourse === courseTitle ? null : courseTitle;
    setExpandedCourse(newExpanded);

    // Reset student search and page when expanding a new course
    if (newExpanded) {
      setStudentSearchTerms(prev => ({ ...prev, [courseTitle]: "" }));
      setStudentPages(prev => ({ ...prev, [courseTitle]: 1 }));
    }
  };

  // Group enrollments by course title
  const getEnrollmentsByCourse = (courseTitle) => {
    if (!enrollmentsData?.getEnrollments) return [];

    return enrollmentsData.getEnrollments.filter(
      (enrollment) => enrollment.courseTitle === courseTitle
    );
  };

  // Get filtered enrollments based on search
  const getFilteredEnrollments = (courseTitle) => {
    const enrollments = getEnrollmentsByCourse(courseTitle);
    const searchTerm = studentSearchTerms[courseTitle] || "";

    if (!searchTerm) return enrollments;

    const lowerSearch = searchTerm.toLowerCase();
    return enrollments.filter(enrollment =>
      enrollment.name.toLowerCase().includes(lowerSearch) ||
      enrollment.email.toLowerCase().includes(lowerSearch) ||
      enrollment.phone.includes(searchTerm) ||
      enrollment.id.toLowerCase().includes(lowerSearch)
    );
  };

  // Get paginated enrollments
  const getPaginatedEnrollments = (courseTitle) => {
    const filtered = getFilteredEnrollments(courseTitle);
    const currentStudentPage = studentPages[courseTitle] || 1;
    const startIndex = (currentStudentPage - 1) * STUDENTS_PER_PAGE;
    const endIndex = startIndex + STUDENTS_PER_PAGE;

    return {
      enrollments: filtered.slice(startIndex, endIndex),
      totalEnrollments: filtered.length,
      currentPage: currentStudentPage,
      totalPages: Math.ceil(filtered.length / STUDENTS_PER_PAGE)
    };
  };

  // Get enrollment count for a course
  const getEnrollmentCount = (courseTitle) => {
    return getEnrollmentsByCourse(courseTitle).length;
  };

  const handleStudentSearch = (courseTitle, value) => {
    setStudentSearchTerms(prev => ({ ...prev, [courseTitle]: value }));
    setStudentPages(prev => ({ ...prev, [courseTitle]: 1 })); // Reset to page 1 on search
  };

  const handleStudentPageChange = (courseTitle, page) => {
    setStudentPages(prev => ({ ...prev, [courseTitle]: page }));
  };

  if (loadingTrainings || loadingEnrollments) {
    return (
      <div className="training-status-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading training data...</p>
        </div>
      </div>
    );
  }

  if (errorTrainings || errorEnrollments) {
    return (
      <div className="training-status-container">
        <div className="error-state">
          <p>❌ Error: {errorTrainings?.message || errorEnrollments?.message}</p>
        </div>
      </div>
    );
  }

  const trainings = trainingsData?.getTrainings || [];

  // Pagination logic for courses
  const totalPages = Math.ceil(trainings.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedTrainings = trainings.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setExpandedCourse(null); // Collapse any expanded rows when changing pages
  };

  const handleSearch = () => {
    setCurrentPage(1); // Reset to first page on search
    setExpandedCourse(null);
    refetchTrainings();
  };

  return (
    <div className="training-status-container">
      <div className="training-header">
        <div className="header-content">
          <h1 className="training-status-title">📚 Training Courses & Enrollments</h1>
          <p className="training-subtitle">Manage courses and view student enrollments</p>
        </div>

        <div className="header-actions">
          <div className="search-container">
            <input
              type="text"
              placeholder="🔍 Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="search-input"
            />
            <button onClick={handleSearch} className="refresh-btn">
              🔍 Search
            </button>
            <button onClick={() => {
              setSearchTerm("");
              setCurrentPage(1);
              setExpandedCourse(null);
              refetchTrainings();
            }} className="refresh-btn">
              🔄 Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Courses Table */}
      <div className="courses-table-container">
        <div className="table-header-info">
          <h3>All Training Courses ({trainings.length})</h3>
          <p className="pagination-info">
            Showing {startIndex + 1} - {Math.min(endIndex, trainings.length)} of {trainings.length} courses
          </p>
        </div>

        {trainings.length === 0 ? (
          <div className="empty-state">
            <p>📭 No training courses found</p>
          </div>
        ) : (
          <>
            <div className="table-wrapper">
              <table className="courses-table">
                <thead>
                  <tr>
                    <th>Course Title</th>
                    <th>Duration</th>
                    <th>Amount</th>
                    <th>GST</th>
                    <th>Total Amount</th>
                   
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTrainings.map((training) => {
                    const enrollmentCount = getEnrollmentCount(training.title);
                    const isExpanded = expandedCourse === training.title;
                    const { enrollments, totalEnrollments, currentPage: studentCurrentPage, totalPages: studentTotalPages } =
                      getPaginatedEnrollments(training.title);
                    const studentSearchTerm = studentSearchTerms[training.title] || "";

                    return (
                      <React.Fragment key={training.id}>
                        {/* Course Row */}
                        <tr className={`course-row ${isExpanded ? 'expanded' : ''}`}>
                          <td>
                            <div className="course-title-cell">
                              <strong>{training.title}</strong>
                              <small>{training.shortDescription}</small>
                            </div>
                          </td>
                          <td>
                            <span className="duration-badge">{training.days} days</span>
                          </td>
                          <td>₹{training.amount.toLocaleString()}</td>
                          <td>{training.gst}%</td>
                          <td>
                            <strong className="total-amount">₹{training.totalAmount.toLocaleString()}</strong>
                          </td>
                          <td>
                            <button
                              className="enrollment-count-btn"
                              onClick={() => toggleCourseExpansion(training.title)}
                            >
                              <span className="count-badge">{enrollmentCount}</span>
                              <span className="count-label">students</span>
                              <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                            </button>
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button
                                className="btn-delete"
                                onClick={() => handleDelete(training.id)}
                                disabled={deleting}
                                title="Delete Course"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Expanded Enrollments Row */}
                        {isExpanded && (
                          <tr className="enrollments-row">
                            <td colSpan="7">
                              <div className="enrollments-container">
                                <div className="enrollments-header">
                                  <h4>📋 Enrolled Students for "{training.title}"</h4>
                                  <span className="enrollments-count">
                                    {totalEnrollments} enrollment{totalEnrollments !== 1 ? 's' : ''}
                                  </span>
                                </div>

                                {/* Student Search Bar */}
                                <div className="student-search-container">
                                  <input
                                    type="text"
                                    placeholder="🔍 Search students by name, email, phone, or ID..."
                                    value={studentSearchTerm}
                                    onChange={(e) => handleStudentSearch(training.title, e.target.value)}
                                    className="student-search-input"
                                  />
                                  {studentSearchTerm && (
                                    <button
                                      className="clear-search-btn"
                                      onClick={() => handleStudentSearch(training.title, "")}
                                      title="Clear search"
                                    >
                                      ✕
                                    </button>
                                  )}
                                </div>

                                {totalEnrollments === 0 ? (
                                  <div className="no-enrollments">
                                    <p>No enrollments found for this course</p>
                                  </div>
                                ) : enrollments.length === 0 ? (
                                  <div className="no-enrollments">
                                    <p>No students found matching "{studentSearchTerm}"</p>
                                  </div>
                                ) : (
                                  <>
                                    <table className="enrollments-table">
                                      <thead>
                                        <tr>
                                       
                                          <th>Student Name</th>
                                          <th>Email</th>
                                          <th>Phone</th>
                                          <th>Amount Paid</th>
                                        
                                          <th>Enrolled On</th>
                                          <th>Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {enrollments.map((enrollment) => (
                                          <tr key={enrollment.id}>

                                            <td>
                                              <strong>{enrollment.name}</strong>
                                            </td>
                                            <td>
                                              <a href={`mailto:${enrollment.email}`} className="email-link">
                                                {enrollment.email}
                                              </a>
                                            </td>
                                            <td>
                                              <a href={`tel:${enrollment.phone}`} className="phone-link">
                                                {enrollment.phone}
                                              </a>
                                            </td>
                                            <td>
                                              <strong className="amount-paid">₹{enrollment.totalAmount.toLocaleString()}</strong>
                                            </td>
                                           
                                            <td>
                                              <span className="date-text">
                                                {new Date(enrollment.createdAt).toLocaleDateString('en-IN', {
                                                  day: '2-digit',
                                                  month: 'short',
                                                  year: 'numeric'
                                                })}
                                              </span>
                                            </td>
                                            <td>
                                              <button
                                                className="btn-delete-student"
                                                onClick={() => handleDeleteEnrollment(enrollment.id, enrollment.name)}
                                                disabled={deletingEnrollment}
                                                title="Delete Enrollment"
                                              >
                                                🗑️
                                              </button>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>

                                    {/* Student Pagination */}
                                    {studentTotalPages > 1 && (
                                      <div className="student-pagination-container">
                                        <button
                                          className="student-pagination-btn"
                                          onClick={() => handleStudentPageChange(training.title, studentCurrentPage - 1)}
                                          disabled={studentCurrentPage === 1}
                                        >
                                          ← Previous
                                        </button>

                                        <div className="student-page-info">
                                          Page {studentCurrentPage} of {studentTotalPages} •
                                          Showing {enrollments.length} of {totalEnrollments} students
                                        </div>

                                        <button
                                          className="student-pagination-btn"
                                          onClick={() => handleStudentPageChange(training.title, studentCurrentPage + 1)}
                                          disabled={studentCurrentPage === studentTotalPages}
                                        >
                                          Next →
                                        </button>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )
                        }
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Course Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Previous
                </button>

                <div className="page-numbers">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      className={`page-btn ${currentPage === page ? 'active' : ''}`}
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </button>
                  ))}
                </div>

                <button
                  className="pagination-btn"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div >
  );
}